import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  CheckCircle as ResolvedIcon,
  Warning as PendingIcon,
  Error as EscalatedIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import axios from 'axios';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

const AdminPoshComplaintTab = ({ user }) => {
  const { tokenManager, getAuthHeaders } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    status: 'Pending',
    priority: 'Medium',
    assigned_to: '',
    internal_committee_notes: '',
    resolution_notes: ''
  });

  useEffect(() => {
    fetchComplaints();
  }, [user]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/posh/complaint/non-anonymous/user/${user?._id || user?.id}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object responses
        if (Array.isArray(data)) {
          setComplaints(data);
        } else if (data.complaints && Array.isArray(data.complaints)) {
          setComplaints(data.complaints);
        } else {
          setComplaints([]);
        }
      } else {
        // If no POSH endpoint exists, show placeholder
        setComplaints([]);
      }
    } catch (error) {
      console.error('Error fetching POSH complaints:', error);
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle view complaint details
  const handleViewComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setEditMode(false);
    setFormData({
      status: complaint.status || 'Pending',
      priority: complaint.priority || 'Medium',
      assigned_to: complaint.assigned_to || '',
      internal_committee_notes: complaint.internal_committee_notes || '',
      resolution_notes: complaint.resolution_notes || ''
    });
    setDialogOpen(true);
  };

  // Handle edit complaint
  const handleEditComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setEditMode(true);
    setFormData({
      status: complaint.status || 'Pending',
      priority: complaint.priority || 'Medium',
      assigned_to: complaint.assigned_to || '',
      internal_committee_notes: complaint.internal_committee_notes || '',
      resolution_notes: complaint.resolution_notes || ''
    });
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const updateData = {
        status: formData.status,
        priority: formData.priority,
        assigned_to: formData.assigned_to || null,
        internal_committee_notes: formData.internal_committee_notes,
        resolution_notes: formData.resolution_notes,
        poshId: selectedComplaint.poshId // Include poshId for soft delete
      };

      await axios.put(
        `${API_URL}/posh/complaint/${selectedComplaint._id}/status`,
        updateData,
        { headers: getAuthHeaders() }
      );

      toast.success('Complaint updated successfully');
      setDialogOpen(false);
      fetchComplaints(); // Refresh the list
    } catch (error) {
      console.error('Error updating complaint:', error);
      toast.error(error.response?.data?.error || 'Failed to update complaint');
    }
  };

  // Download supporting document
  const downloadDocument = async (complaintId, fileName, originalName) => {
    try {
      const response = await axios.get(
        `${API_URL}/posh/complaint/${complaintId}/document/${fileName}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 404) {
        toast.error('Document not found');
      } else {
        toast.error('Failed to download document');
      }
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
        return <ResolvedIcon color="success" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'under_investigation':
        return <EscalatedIcon color="error" />;
      case 'closed':
        return <ResolvedIcon color="success" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved': return 'success';
      case 'pending': return 'warning';
      case 'under_investigation': return 'error';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  // Format date using utility function
  const formatDateLocal = (dateString) => {
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {user?.firstName} {user?.lastName}'s POSH Complaints
        </Typography>
      </Box>
      
      {complaints.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No POSH complaints found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This user has not submitted any POSH complaints.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>POSH ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Incident Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complaints.map((complaint, index) => (
                <TableRow key={complaint._id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {complaint.poshId || `#${index + 1}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                            <Typography variant="body2" color="text.secondary">
                      {complaint.type_of_incident || 'No incident type'}
                            </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={complaint.priority || 'Medium'}
                      color={complaint.priority?.toLowerCase() === 'high' ? 'error' : complaint.priority?.toLowerCase() === 'low' ? 'success' : 'warning'}
                      size="small"
                      sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateLocal(complaint.created_at || complaint.createdAt)}
                            </Typography>
                  </TableCell>
                  <TableCell>
                          <Chip 
                      icon={getStatusIcon(complaint.status)}
                      label={complaint.status || 'Pending'}
                            color={getStatusColor(complaint.status)}
                            size="small"
                      sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewComplaint(complaint)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          onClick={() => handleEditComplaint(complaint)}
                          color="secondary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* POSH Complaint Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Update POSH Complaint Status' : 'POSH Complaint Details'}
        </DialogTitle>
        <DialogContent>
          {selectedComplaint && (
            <Box sx={{ mt: 2 }}>
              {/* Employee Information */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', fontSize: '20px' }}>
                    Employee Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Submission Type:</strong> Non-Anonymous
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Full Name:</strong> {selectedComplaint.name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Employee ID:</strong> {selectedComplaint.employee_id || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Department:</strong> {selectedComplaint.department || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Designation:</strong> {selectedComplaint.designation || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Contact Number:</strong> {selectedComplaint.contact_number || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Email:</strong> {selectedComplaint.email || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Are you comfortable being contacted by the Internal Committee?</strong> {selectedComplaint.comfortable_contact || 'N/A'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Details of the Alleged Harasser
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    (Person against whom the complaint is being made)
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Name:</strong> {selectedComplaint.harasser_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Department:</strong> {selectedComplaint.harasser_department || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Designation:</strong> {selectedComplaint.harasser_designation || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Do you work directly with this person for your regular work activities?</strong> {selectedComplaint.direct_work_relation || 'N/A'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Complaint Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Type of Incident:</strong> {selectedComplaint.type_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Incident:</strong> {formatDateLocal(selectedComplaint.date_of_incident)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time of Incident:</strong> {selectedComplaint.time_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {selectedComplaint.location_of_incident || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Harasser Name:</strong> {selectedComplaint.harasser_name || 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Is this the first occurrence?</strong> {selectedComplaint.first_occurrence || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.first_occurrence === 'no' && selectedComplaint.previous_incidents && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If No, provide details of previous incidents:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.previous_incidents}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Were there any witnesses?</strong> {selectedComplaint.witnesses || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.witnesses === 'yes' && selectedComplaint.witnesses_details && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, please provide names/contact info:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.witnesses_details}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Incident Description
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Incident Description:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {selectedComplaint.incident_description || 'No description provided'}
                  </Typography>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Supporting Evidence (if any)
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Documents, emails, text messages, screenshots, etc. attached?</strong> {selectedComplaint.has_supporting_evidence || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.has_supporting_evidence === 'yes' && selectedComplaint.supporting_evidence && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, list or describe them briefly:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.supporting_evidence}
                    </Typography>
                  </Grid>
                )}
                
                {/* Supporting Documents */}
                {selectedComplaint.supporting_documents && 
                 selectedComplaint.supporting_documents.length > 0 && 
                 selectedComplaint.supporting_documents.some(doc => doc.fileName && doc.originalName) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Supporting Documents ({selectedComplaint.supporting_documents.filter(doc => doc.fileName && doc.originalName).length}):
                    </Typography>
                    <List dense>
                      {selectedComplaint.supporting_documents
                        .filter(doc => doc.fileName && doc.originalName)
                        .map((doc, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <AttachFileIcon sx={{ mr: 1, color: 'primary.main', fontSize: 16 }} />
                          <ListItemText
                            primary={doc.originalName}
                            secondary={doc.fileSize ? `${(doc.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Size not available'}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                          />
                          <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                              aria-label="download"
                              onClick={() => downloadDocument(selectedComplaint._id, doc.fileName, doc.originalName)}
                            color="primary"
                              size="small"
                          >
                              <DownloadIcon />
                          </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Actions Already Taken (if any)
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Did you inform anyone about the incident?</strong> {selectedComplaint.informed_someone || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.informed_someone === 'yes' && selectedComplaint.who_informed && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, who did you inform and when?</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.who_informed}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Have any steps been taken (formally/informally) to resolve the matter?</strong> {selectedComplaint.steps_taken_flag || 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.steps_taken_flag === 'yes' && selectedComplaint.steps_taken_details && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>If yes, please explain:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.steps_taken_details}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Preferred Outcome or Resolution
                  </Typography>
                </Grid>
                
                {selectedComplaint.preferred_resolution && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Do you have any preferences regarding how this should be resolved or addressed?</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.preferred_resolution}
                    </Typography>
                  </Grid>
                )}
                
                <Divider sx={{ my: 3 }} />
                
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary', mt: 3, fontSize: '20px' }}>
                    Reporting Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date of Filing the Complaint:</strong> {selectedComplaint.date_of_filing ? formatDateLocal(selectedComplaint.date_of_filing) : 'N/A'}
                  </Typography>
                </Grid>
                
                {selectedComplaint.reason_for_delay && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reason for delay in filing (if beyond 3 months):</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedComplaint.reason_for_delay}
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Is your Manager/HR informed about the incident?</strong> {selectedComplaint.manager_informed || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Action Taken History - Show in both view and edit modes */}
              {selectedComplaint.actionTakenHistory && selectedComplaint.actionTakenHistory.length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '18px' }}>
                      Action Taken History
                    </Typography>
                    <Box sx={{ 
                      maxHeight: '400px', 
                      overflowY: 'auto',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'grey.50'
                    }}>
                      {selectedComplaint.actionTakenHistory.map((action, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: action.deletedAt ? 'grey.50' : 'info.50', 
                            borderRadius: 2, 
                            border: '1px solid', 
                            borderColor: action.deletedAt ? 'grey.300' : 'info.300',
                            opacity: action.deletedAt ? 0.8 : 1,
                            boxShadow: action.deletedAt ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip
                                  label={action.status}
                                  color={action.status === 'Resolved' ? 'success' : action.status === 'Escalated' ? 'error' : 'warning'}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                <Chip
                                  label={action.priority}
                                  color={action.priority === 'Critical' ? 'error' : action.priority === 'High' ? 'warning' : 'info'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 'bold' }}
                                />
                                {action.deletedAt && (
                                  <Chip
                                    label="Previous Update"
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}>
                                {formatDateTime(action.created_at)}
                              </Typography>
                            </Box>
                            {action.internal_committee_notes && (
                              <Typography variant="body2" sx={{ 
                                mt: 1,
                                lineHeight: 1.6,
                                color: 'text.primary'
                              }}>
                                <strong>Internal Committee Notes:</strong> {action.internal_committee_notes}
                              </Typography>
                            )}
                            {action.resolution_notes && (
                              <Typography variant="body2" sx={{ 
                                mt: 1,
                                lineHeight: 1.6,
                                color: 'text.primary'
                              }}>
                                <strong>Resolution Notes:</strong> {action.resolution_notes}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Admin Section */}
              {editMode && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '20px' }}>
                      Admin Section
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            label="Status"
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Under Investigation">Under Investigation</MenuItem>
                            <MenuItem value="Resolved">Resolved</MenuItem>
                            <MenuItem value="Escalated">Escalated</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Priority</InputLabel>
                          <Select
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                            label="Priority"
                          >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Critical">Critical</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Internal Committee Notes"
                          value={formData.internal_committee_notes}
                          onChange={(e) => setFormData({ ...formData, internal_committee_notes: e.target.value })}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Resolution Notes"
                          value={formData.resolution_notes}
                          onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Current Admin Status (View Mode) */}
              {!editMode && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontSize: '20px' }}>
                      Current Status
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Status:</strong>
                        </Typography>
                        <Chip
                          icon={getStatusIcon(selectedComplaint.status)}
                          label={selectedComplaint.status}
                          color={getStatusColor(selectedComplaint.status)}
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Priority:</strong>
                        </Typography>
                        <Chip
                          label={selectedComplaint.priority || 'Medium'}
                          color={selectedComplaint.priority?.toLowerCase() === 'high' ? 'error' : selectedComplaint.priority?.toLowerCase() === 'low' ? 'success' : 'warning'}
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      {selectedComplaint.assigned_to && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Assigned To:</strong> {selectedComplaint.assigned_to.fullName || selectedComplaint.assigned_to.email}
                          </Typography>
                        </Grid>
                      )}
                      
                      {/* Current Admin Notes (if no history) */}
                      {(!selectedComplaint.actionTakenHistory || selectedComplaint.actionTakenHistory.length === 0) && (
                        <>
                          {selectedComplaint.internal_committee_notes && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Internal Committee Notes:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                {selectedComplaint.internal_committee_notes}
                              </Typography>
                            </Grid>
                          )}
                          {selectedComplaint.resolution_notes && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Resolution Notes:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                {selectedComplaint.resolution_notes}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {editMode && (
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPoshComplaintTab;
